/**
 * Discrete vs Continuous Relationships generator.
 *
 * A real-world scenario is presented and the student classifies the
 * relationship as discrete (countable, separate values) or continuous (any
 * value in a range). Multiple choice with a fixed, hand-classified scenario
 * table so the correct answer is always well-defined - there is no runtime
 * math to get wrong, only a curated pairing of scenario to classification.
 *
 * Families:
 *   S1: classify the scenario   -> choice (discrete / continuous)
 */

import { Rng } from "../engine/rng";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Choice,
  Question,
  QuestionSignature,
} from "./types";
import { getTopic } from "../curriculum/curriculum";

const TOPIC = "discrete-continuous" as const;
const VERSION = "1.0.0";

type Kind = "discrete" | "continuous";

interface Scenario {
  readonly text: string;
  readonly kind: Kind;
  readonly difficulty: Difficulty;
}

// Hand-classified scenarios. Discrete = counted in whole units; continuous =
// measured and can take any value in a range.
const SCENARIOS: readonly Scenario[] = [
  { text: "The number of students in each classroom", kind: "discrete", difficulty: "easy" },
  { text: "The temperature of a cup of coffee over time", kind: "continuous", difficulty: "easy" },
  { text: "The number of cars in a parking lot each hour", kind: "discrete", difficulty: "easy" },
  { text: "The height of a plant measured each day", kind: "continuous", difficulty: "easy" },
  { text: "The total cost of buying x whole concert tickets", kind: "discrete", difficulty: "standard" },
  { text: "The distance a car travels over time", kind: "continuous", difficulty: "standard" },
  { text: "The number of pets owned by families on a street", kind: "discrete", difficulty: "standard" },
  { text: "The amount of water in a tank as it drains", kind: "continuous", difficulty: "standard" },
  { text: "The number of goals scored in each soccer game", kind: "discrete", difficulty: "standard" },
  { text: "A person's weight measured each month", kind: "continuous", difficulty: "hard" },
  { text: "The number of books on each shelf of a library", kind: "discrete", difficulty: "hard" },
  { text: "The speed of a runner during a race", kind: "continuous", difficulty: "hard" },
  { text: "The number of chairs needed for x tables of 4", kind: "discrete", difficulty: "hard" },
  { text: "The length of a burning candle over time", kind: "continuous", difficulty: "challenge" },
  { text: "The number of buses needed to carry x students", kind: "discrete", difficulty: "challenge" },
  { text: "The voltage across a charging capacitor over time", kind: "continuous", difficulty: "challenge" },
];

function eligible(difficulty: Difficulty): readonly Scenario[] {
  const order: Difficulty[] = ["easy", "standard", "hard", "challenge"];
  const cap = order.indexOf(difficulty);
  const pool = SCENARIOS.filter((s) => order.indexOf(s.difficulty) <= cap);
  return pool.length >= 2 ? pool : SCENARIOS;
}

export class DiscreteContinuousGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = ["easy", "standard", "hard", "challenge"];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    const rng = new Rng(ctx.seed);
    const scenario = rng.pick(eligible(ctx.difficulty));

    // Fixed two-option choice; shuffle order for variety.
    const options: Choice[] = [
      { id: scenario.kind === "discrete" ? "correct" : "d0", label: "Discrete" },
      { id: scenario.kind === "continuous" ? "correct" : "d1", label: "Continuous" },
    ];
    const choices = rng.shuffle(options);

    return {
      id: `${TOPIC}-${ctx.seed}-${scenario.kind}`,
      generator: TOPIC,
      generatorVersion: VERSION,
      seed: ctx.seed,
      unit: topic.unit,
      topic: TOPIC,
      family: scenario.kind,
      difficulty: ctx.difficulty,
      prompt: {
        text: `Is this relationship discrete or continuous? "${scenario.text}"`,
        latex: `\\text{Is this discrete or continuous? “${scenario.text}”}`,
      },
      answerType: "choice",
      answer: { kind: "choice", value: "correct" },
      choices,
      steps: [
        {
          description:
            scenario.kind === "discrete"
              ? "The quantity is counted in whole, separate units, so it is discrete."
              : "The quantity is measured and can take any value in a range, so it is continuous.",
          latex: scenario.kind === "discrete" ? "\\text{discrete}" : "\\text{continuous}",
        },
      ],
      hints: [
        { level: 1, text: "Can the quantity take any value in between, or only separate whole values?" },
        { level: 2, text: "Counted things are discrete; measured things are usually continuous." },
      ],
      subskills: topic.subskills,
    };
  }

  signature(q: Question): QuestionSignature {
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure: q.id };
  }
}

export const discreteContinuous = new DiscreteContinuousGenerator();
